// assets/AllCourses/Exercises/CompleteTheSentencesExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function CompleteTheSentencesExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(
    exercise.sentenceCompletionQuestions
  )
    ? exercise.sentenceCompletionQuestions
    : [];

  /* ================= STUDENT STATE ================= */
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setAnswers({});
    completedRef.current = false;
  }, [exercise._id]);

  /* ================= PREVIEW RESTORE ================= */
  useEffect(() => {
    if (!isPreview || !exercise?.attempt) return;
    setAnswers(exercise.attempt.answers || {});
  }, [isPreview, exercise?.attempt]);

  /* ================= RESET COMPLETE FLAG ================= */
  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", cap = false) => {
    let t = String(text).trim();
    if (!cap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode)
      onUpdate("sentenceCompletionQuestions", next);
  };

  const updateQuestionField = (idx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[idx] || {
      text: "",
      options: [],
      answer: "",
      requireCapitalization: false,
    };

    if (field === "options") {
      const options = value
        .split(",")
        .map(o => o.trim())
        .filter(Boolean);

      next[idx] = {
        ...current,
        options,
        answer: options.includes(current.answer)
          ? current.answer
          : "",
      };
    } else {
      next[idx] = { ...current, [field]: value };
    }

    updateQuestions(next);
  };

  /* ================= STUDENT INPUT ================= */
  const handleChange = (qIdx, value) => {
    if (isEditMode || showResults) return;

    const updated = { ...answers, [qIdx]: value };
    setAnswers(updated);

    // 🔥 ALWAYS update parent (for progress + submit enable)
    onComplete(updated, false);

    const allAnswered = questions.every(
      (_, i) => updated[i] !== undefined
    );

    if (allAnswered) {
      checkAnswers(updated);
    }
  };

  /* ================= CHECK ANSWERS ================= */
  const checkAnswers = (finalAnswers) => {
    const allCorrect = questions.every((q, idx) => {
      const user = normalize(
        finalAnswers[idx],
        q.requireCapitalization
      );
      const correct = normalize(
        q.answer,
        q.requireCapitalization
      );

      return user === correct;
    });

    onComplete(finalAnswers, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6">
            <div className="space-y-4">
              <TextareaField
                label={`Sentence ${idx + 1}`}
                value={q.text || ""}
                onChange={(v) =>
                  updateQuestionField(idx, "text", v)
                }
                placeholder="She ___ to school every day."
                rows={3}
                required
                error={
                  errors?.[
                    `exercise-${exercise.index}-sc-text-${idx}`
                  ]
                }
              />

              <StringField
                label="Options (comma separated)"
                value={(q.options || []).join(", ")}
                onChange={(v) =>
                  updateQuestionField(idx, "options", v)
                }
                placeholder="goes, go, going"
                required
              />

              <StringField
                label="Correct Answer"
                value={q.answer || ""}
                onChange={(v) =>
                  updateQuestionField(idx, "answer", v)
                }
                placeholder="goes"
                required
              />

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) =>
                  updateQuestionField(
                    idx,
                    "requireCapitalization",
                    c
                  )
                }
              />

              <Button
                size="sm"
                variant="destructive"
                onClick={() =>
                  updateQuestions(
                    questions.filter((_, i) => i !== idx)
                  )
                }
              >
                Remove Question
              </Button>
            </div>
          </Card>
        ))}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                text: "",
                options: [],
                answer: "",
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No sentences available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Complete the Sentences
        </CardTitle>
        <CardDescription>
          Choose the correct option to complete each sentence.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {questions.map((q, qIdx) => (
          <div key={qIdx} className="space-y-4">
            {/* Sentence */}
            <div className="rounded-xl border bg-muted/40 p-5 font-medium">
              {q.text}
            </div>

            {/* Options */}
            <RadioGroup
              value={answers[qIdx] || ""}
              onValueChange={(v) =>
                handleChange(qIdx, v)
              }
              disabled={showResults}
              className="space-y-3"
            >
              {q.options.map((option, oIdx) => {
                const selected =
                  answers[qIdx] === option;

                const correct =
                  normalize(option, q.requireCapitalization) ===
                  normalize(q.answer, q.requireCapitalization);

                return (
                  <div
                    key={oIdx}
                    className={cn(
                      "flex items-center gap-3 rounded-lg border p-4 transition",

                      // before submit
                      selected && !showResults && "bg-accent",

                      // correct option ALWAYS green
                      showResults &&
                        correct &&
                        "bg-green-100 border-green-400",

                      // wrong selected → red
                      showResults &&
                        selected &&
                        !correct &&
                        "bg-red-100 border-red-400"
                    )}
                  >
                    <RadioGroupItem
                      value={option}
                      id={`cts-${qIdx}-${oIdx}`}
                    />

                    <Label
                      htmlFor={`cts-${qIdx}-${oIdx}`}
                      className="flex-1 cursor-pointer"
                    >
                      {option}

                      {showResults && selected && (
                        <span className="ml-2 text-sm">
                          {correct
                            ? "Correct"
                            : `Correct: ${q.answer}`}
                        </span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}